import React from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { BlogApiData } from "..";
import { Blog_Status_Enum } from "../../../../api/generated/graphql";

interface HeroSectionProps {
  formData: BlogApiData["al_sections"]["heroSection"];
  slug: string;
  status: Blog_Status_Enum;
  onSectionChange: (section: keyof BlogApiData["al_sections"], field: string, value: string) => void;
  onBasicFieldChange: (field: keyof Pick<BlogApiData["al_sections"]["heroSection"], "title" | "short_title" | "minRead" | "description" | "image_description">, value: string) => void;
  onSlugChange: (slug: string) => void;
  onStatusChange: (status: Blog_Status_Enum) => void;
  onImageUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isUploadingImage: boolean;
  quillModules: any;
  quillFormats: string[];
}

const HeroSection: React.FC<HeroSectionProps> = ({
  formData,
  slug,
  status,
  onSectionChange,
  onBasicFieldChange,
  onSlugChange,
  onStatusChange,
  onImageUpload,
  isUploadingImage,
  quillModules,
  quillFormats,
}) => {
  return (
    <div className="space-y-6">
      {/* Basic Information Fields */}
      <div className="space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Blog Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) =>
                onBasicFieldChange("title", e.target.value)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter blog title"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Short Title
            </label>
            <input
              type="text"
              value={formData.short_title}
              onChange={(e) =>
                onBasicFieldChange("short_title", e.target.value)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Short version of title"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Slug *
            </label>
            <input
              type="text"
              value={slug}
              onChange={(e) => onSlugChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="url-friendly-slug"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              value={status}
              onChange={(e) => onStatusChange(e.target.value as Blog_Status_Enum)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value={Blog_Status_Enum.Draft}>Draft</option>
              <option value={Blog_Status_Enum.Published}>Published</option>
              <option value={Blog_Status_Enum.Archived}>Archived</option>
            </select>
          </div>
        </div>
      </div>

      {/* Hero Section Fields */}
      <div className="space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Min Read Time
            </label>
            <input
              type="text"
              value={formData.minRead}
              onChange={(e) =>
                onSectionChange(
                  "heroSection",
                  "minRead",
                  e.target.value
                )
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., 5 min read"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Hero Description *
          </label>
          <ReactQuill
            theme="snow"
            value={formData.description}
            onChange={(value) =>
              onSectionChange("heroSection", "description", value)
            }
            modules={quillModules}
            formats={quillFormats}
            placeholder="Hero section description"
          />
        </div>
      </div>

      {/* Hero Image Section */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Hero Image
          </label>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Hero Image
              </label>
              <div className="relative">
                <input
                  type="file"
                  accept="image/*"
                  onChange={onImageUpload}
                  disabled={isUploadingImage}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                />
                {isUploadingImage && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 rounded-md">
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      <span className="text-xs text-gray-600">
                        Uploading...
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            {formData.image_url && !isUploadingImage && (
              <div className="flex items-center space-x-4 p-3 bg-gray-50 rounded-md">
                <img
                  src={formData.image_url}
                  alt="Hero Image Preview"
                  className="w-24 h-16 object-cover rounded border"
                />
                <div>
                  <p className="text-sm text-gray-700">
                    Hero Image Preview
                  </p>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSectionChange(
                        "heroSection",
                        "image_url",
                        ""
                      );
                    }}
                    className="text-xs text-red-600 hover:text-red-800">
                    Remove image
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Image Description (Alt Text)
          </label>
          <input
            type="text"
            value={formData.image_description}
            onChange={(e) =>
              onBasicFieldChange("image_description", e.target.value)
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="e.g., Beautiful view of Tirana city center"
          />
        </div>
      </div>
    </div>
  );
};

export default HeroSection;
